import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { inputClass } from "./FormField";
import {
  fetchAuditorHospitalActs,
  type AuditorHospitalMedicalAct,
} from "../api/auditorApi";

type TariffActAutocompleteProps = {
  hospitalId: number | null | undefined;
  tariffs?: AuditorHospitalMedicalAct[];
  value: string;
  currency: string;
  onChange: (value: string) => void;
  onSelect: (act: AuditorHospitalMedicalAct) => void;
  className?: string;
};

export function TariffActAutocomplete({
  hospitalId,
  tariffs,
  value,
  currency,
  onChange,
  onSelect,
  className,
}: TariffActAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remoteOptions, setRemoteOptions] = useState<AuditorHospitalMedicalAct[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const decimals = currency === "CDF" ? 0 : 2;
  const useLocal = Array.isArray(tariffs) && tariffs.length > 0;

  const localOptions = useCallback(() => {
    if (!useLocal || !tariffs) return [];
    const q = value.trim().toLowerCase();
    if (q.length < 2) return [];
    return tariffs
      .filter(
        (act) =>
          act.label.toLowerCase().includes(q) ||
          act.code.toLowerCase().includes(q),
      )
      .slice(0, 10);
  }, [tariffs, useLocal, value]);

  const options = useLocal ? localOptions() : remoteOptions;

  useEffect(() => {
    if (useLocal) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = value.trim();
    if (q.length < 2 || !hospitalId) {
      setRemoteOptions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void (async () => {
        setLoading(true);
        try {
          const result = await fetchAuditorHospitalActs(hospitalId, { q });
          setRemoteOptions(result.medical_acts.slice(0, 10));
        } catch {
          setRemoteOptions([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, hospitalId, useLocal]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className={cn(inputClass, "text-xs")}
        placeholder={
          hospitalId || useLocal ? "Chercher grille…" : "Hôpital non renseigné"
        }
        disabled={!hospitalId && !useLocal}
        autoComplete="off"
      />
      {open && (loading || options.length > 0) && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {loading && <p className="px-2 py-1.5 text-[11px] text-muted">Recherche…</p>}
          {!loading &&
            options.map((act) => (
              <button
                key={act.id}
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs hover:bg-card-muted"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(act);
                  setOpen(false);
                }}
              >
                <span className="truncate font-medium">{act.label}</span>
                <span className="shrink-0 font-semibold text-green-700">
                  {act.cost.toLocaleString("fr-FR", {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                  })}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
